import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Plus, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export default function TimetableManagement() {
  const [entries, setEntries] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [filterClass, setFilterClass] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ class_id: '', subject_id: '', teacher_id: '', day: 'Monday', period: 1, start_time: '08:00', end_time: '08:45', room: '' });

  const load = () => {
    const params = filterClass ? `?class_id=${filterClass}` : '';
    api.get(`/admin/timetable${params}`).then(r => setEntries(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filterClass]);
  useEffect(() => {
    api.get('/admin/classes').then(r => setClasses(r.data)).catch(() => {});
    api.get('/admin/subjects').then(r => setSubjects(r.data)).catch(() => {});
    api.get('/admin/teachers').then(r => setTeachers(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    try {
      await api.post('/admin/timetable', form);
      toast.success('Timetable entry added');
      setShowDialog(false); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.clashes) {
        detail.clashes.forEach(c => toast.error(c, { duration: 6000 }));
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Error');
      }
    }
  };

  // Group by day
  const grouped = DAYS.reduce((acc, day) => {
    acc[day] = entries.filter(e => e.day === day).sort((a, b) => a.period - b.period);
    return acc;
  }, {});

  return (
    <div data-testid="timetable-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-[#1B2A4A] flex items-center gap-2"><Calendar className="w-6 h-6" /> Timetable</h1>
        <div className="flex gap-3">
          <Select value={filterClass} onValueChange={v => setFilterClass(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48" data-testid="filter-class-select"><SelectValue placeholder="All Classes" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Classes</SelectItem>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => setShowDialog(true)} className="bg-[#1B2A4A]" data-testid="add-timetable-btn"><Plus className="w-4 h-4 mr-1" /> Add Entry</Button>
        </div>
      </div>

      {/* Timetable Grid */}
      {DAYS.map(day => (
        <div key={day} className="mb-6">
          <h3 className="font-bold text-[#1B2A4A] mb-2">{day}</h3>
          {grouped[day].length === 0 ? (
            <p className="text-sm text-slate-400 pl-4">No entries</p>
          ) : (
            <div className="bg-white rounded-xl border shadow-sm">
              <Table>
                <TableHeader><TableRow className="data-table"><TableHead>Period</TableHead><TableHead>Time</TableHead><TableHead>Subject</TableHead><TableHead>Teacher</TableHead><TableHead>Class</TableHead><TableHead>Room</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {grouped[day].map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="font-bold">{e.period}</TableCell>
                      <TableCell className="text-sm">{e.start_time} - {e.end_time}</TableCell>
                      <TableCell className="font-semibold">{e.subject_name}</TableCell>
                      <TableCell>{e.teacher_name}</TableCell>
                      <TableCell>{e.class_name}</TableCell>
                      <TableCell>{e.room}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/admin/timetable/${e.id}`); toast.success('Deleted'); load(); }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ))}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Timetable Entry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Class</Label><Select value={form.class_id} onValueChange={v => setForm({...form, class_id: v})}><SelectTrigger data-testid="tt-class-select"><SelectValue placeholder="Select Class" /></SelectTrigger><SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Subject</Label><Select value={form.subject_id} onValueChange={v => setForm({...form, subject_id: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Teacher</Label><Select value={form.teacher_id} onValueChange={v => setForm({...form, teacher_id: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Day</Label><Select value={form.day} onValueChange={v => setForm({...form, day: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Period</Label><Input type="number" min={1} max={10} value={form.period} onChange={e => setForm({...form, period: parseInt(e.target.value)||1})} /></div>
            <div><Label>Start Time</Label><Input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} /></div>
            <div><Label>End Time</Label><Input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} /></div>
            <div className="col-span-2"><Label>Room</Label><Input value={form.room} onChange={e => setForm({...form, room: e.target.value})} placeholder="e.g., Room 101" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={handleSave} className="bg-[#1B2A4A]" data-testid="save-timetable-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
